import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Input } from "@faire/web--source/slate/Input";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { IFindBusinessAddressRequest } from "@faire/web-api--source/indigofair/data/IFindBusinessAddressRequest";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import { ChangeEventHandler, useEffect, useRef, useState } from "react";
import * as React from "react";
import styled from "styled-components";

import {
  CustomAddressDropdown,
  IAddressOption,
} from "@faire/retailer/components/GoogleMapsComponent/CustomAddressDropdown";
import { SampleForm } from "@faire/retailer/features/ProspectingWithSamples/ProspectingSampleSignupFlow";
import { useAutoComplete } from "@faire/retailer/features/ProspectingWithSamples/useAutoComplete";

interface AutoCompleteStoreNameProps {
  formValues: SampleForm;
  setFieldValue: (field: string, value: string | IMatchedBusiness) => void;
  setFormValues: (formValues: SampleForm) => void;
  error?: string | boolean;
}

export const AutoCompleteStoreName: React.FC<AutoCompleteStoreNameProps> = ({
  formValues,
  setFieldValue,
  setFormValues,
  error,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    autoCompleteResults,
    areBusinessesLoading,
    getBusinessAddress,
  } = useAutoComplete(formValues.storeName, formValues.country);

  const [resetAutofilledAddress, setResetAutofilledAddress] = useState(false);

  useEffect(() => {
    if (resetAutofilledAddress) {
      clearAddressFields(setFieldValue, setFormValues, formValues);
      // reset store name:
      setFieldValue("storeName", "");
      setFormValues({ ...formValues, storeName: "" });
      setResetAutofilledAddress(false);
    }
  }, [resetAutofilledAddress, setFieldValue, setFormValues, formValues]);

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setResetAutofilledAddress(true);
    }
  }, [formValues.country]);

  const selectBusiness = (option: IAddressOption) => {
    const storeName = option.name ?? "";
    const matchedBusiness = IMatchedBusiness.build({
      ...option,
      type: IMatchedBusiness.Type.GOOGLE_PLACES,
    });
    // now that user selected a business, we need to get the address,
    // First clear the address fields:
    clearAddressFields(setFieldValue, setFormValues, formValues);
    // and get the address:
    getBusinessAddress(
      [
        IFindBusinessAddressRequest.build({
          business: matchedBusiness,
        }),
      ],
      {
        onSuccess: (response) => {
          if (response.address) {
            const {
              address1 = "",
              address2 = "",
              city = "",
              state = "",
              postal_code: zipcode = "",
            } = response.address;
            setFieldValue("address1", address1);
            setFieldValue("address2", address2);
            setFieldValue("city", city);
            setFieldValue("state", state);
            setFieldValue("zipcode", zipcode);
            setFormValues({
              ...formValues,
              address1,
              address2,
              city,
              state,
              zipcode,
            });
          }
        },
      }
    );
    setFieldValue("storeName", storeName);
    setFieldValue("matchedBusiness", matchedBusiness);
    setFormValues({ ...formValues, storeName, matchedBusiness });
    setIsDropdownOpen(false);
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const matchedBusiness = IMatchedBusiness.build({
      type: IMatchedBusiness.Type.MANUAL,
      name: e.target.value,
    });
    setFieldValue("storeName", e.target.value);
    setFieldValue("matchedBusiness", matchedBusiness);
    setFormValues({
      ...formValues,
      matchedBusiness,
      storeName: e.target.value,
    });
    if (e.target.value.length > 0) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  return (
    <CustomAddressDropdown
      open={isDropdownOpen}
      onOpen={() => setIsDropdownOpen(true)}
      onClose={() => setIsDropdownOpen(false)}
      options={autoCompleteResults}
      onSelect={selectBusiness}
      showCannotFindOption
      onCannotFindClick={() => {
        clearAddressFields(setFieldValue, setFormValues, formValues);
        setIsDropdownOpen(false);
      }}
    >
      <Input
        type="text"
        onChange={handleInputChange}
        label={strictLocalize({
          defaultMessage: "Store Name",
          description: {
            text: "Label for the input where user enters their store name.",
            img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
          },
        })}
        value={formValues.storeName}
        error={error}
      />
      {areBusinessesLoading ? <StyledLoadingSpinner size="small" /> : null}
    </CustomAddressDropdown>
  );
};

const clearAddressFields = (
  setFieldValue: (field: string, value: string | IMatchedBusiness) => void,
  setFormValues: (formValues: SampleForm) => void,
  formValues: SampleForm
) => {
  setFieldValue("address1", "");
  setFieldValue("address2", "");
  setFieldValue("city", "");
  setFieldValue("state", "");
  setFieldValue("zipcode", "");
  setFieldValue("phoneNumber", "");
  setFormValues({
    ...formValues,
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    phoneNumber: "",
  });
};

const StyledLoadingSpinner = styled(LoadingSpinner)`
  position: absolute;
  top: 32px;
  right: 12px;
`;
